/**
 * WEB-872 (умбрелла WEB-781) — pure color-contrast helpers shared by
 * themes/index.ts and themes/presets.ts. Extracted out of index.ts so
 * presets.ts (or any other themes/* module) can depend on hexLuminance-based
 * derivation without creating a circular import with index.ts (which
 * re-exports THEMES from presets.ts).
 */

/** Разобранный цвет: составляющие 0..255 и прозрачность 0..1. */
export interface ParsedCssColor {
  r: number;
  g: number;
  b: number;
  /** 1 — непрозрачный. Форма без альфы всегда даёт 1. */
  alpha: number;
}

/**
 * Базовые имена цветов CSS. Полный список из 148 имён тут не нужен и вреден:
 * это словарь для ЗАМЕРА яркости, а не парсер CSS. Взяты 16 имён исходного
 * набора HTML + самые ходовые из расширенного — ровно те, которыми модель и
 * авторы пишут «сделай экран тёмным» (`black`, `navy`, `charcoal`-подобные
 * оттенки серого). Неизвестное имя = «мерить нечем», см. `parseCssColorToRgb`.
 */
const NAMED_COLORS: Record<string, readonly [number, number, number]> = {
  black: [0, 0, 0],
  silver: [192, 192, 192],
  gray: [128, 128, 128],
  grey: [128, 128, 128],
  white: [255, 255, 255],
  maroon: [128, 0, 0],
  red: [255, 0, 0],
  purple: [128, 0, 128],
  fuchsia: [255, 0, 255],
  magenta: [255, 0, 255],
  green: [0, 128, 0],
  lime: [0, 255, 0],
  olive: [128, 128, 0],
  yellow: [255, 255, 0],
  navy: [0, 0, 128],
  blue: [0, 0, 255],
  teal: [0, 128, 128],
  aqua: [0, 255, 255],
  cyan: [0, 255, 255],
  orange: [255, 165, 0],
  pink: [255, 192, 203],
  brown: [165, 42, 42],
  gold: [255, 215, 0],
  beige: [245, 245, 220],
  ivory: [255, 255, 240],
  indigo: [75, 0, 130],
  violet: [238, 130, 238],
  crimson: [220, 20, 60],
  coral: [255, 127, 80],
  salmon: [250, 128, 114],
  turquoise: [64, 224, 208],
  tan: [210, 180, 140],
  khaki: [240, 230, 140],
  lavender: [230, 230, 250],
  darkgray: [169, 169, 169],
  darkgrey: [169, 169, 169],
  lightgray: [211, 211, 211],
  lightgrey: [211, 211, 211],
  dimgray: [105, 105, 105],
  dimgrey: [105, 105, 105],
  whitesmoke: [245, 245, 245],
  snow: [255, 250, 250],
};

/** hsl → rgb, каноничная формула (h в градусах, s/l — доли 0..1). */
function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const hh = (((h % 360) + 360) % 360) / 60;
  const x = c * (1 - Math.abs((hh % 2) - 1));
  const [r1, g1, b1] =
    hh < 1
      ? [c, x, 0]
      : hh < 2
        ? [x, c, 0]
        : hh < 3
          ? [0, c, x]
          : hh < 4
            ? [0, x, c]
            : hh < 5
              ? [x, 0, c]
              : [c, 0, x];
  const m = l - c / 2;
  return [
    Math.round((r1 + m) * 255),
    Math.round((g1 + m) * 255),
    Math.round((b1 + m) * 255),
  ];
}

/** Числа внутри скобок функции цвета: запятые и/или пробелы, альфа через «/». */
function functionArgs(inside: string): number[] | null {
  const parts = inside
    .replace(/\//g, ' ')
    .split(/[\s,]+/)
    .filter((p) => p.length > 0);
  const nums = parts.map((p) =>
    p.endsWith('%') ? Number(p.slice(0, -1)) / 100 : Number(p),
  );
  return nums.every((n) => Number.isFinite(n)) ? nums : null;
}

/**
 * WEB-1805, ревизия (Б1) — ОДИН разбор цвета на весь фронт.
 *
 * Схема принимает не только код цвета: `black`, `rgb()/rgba()`, `hsl()/hsla()`,
 * `#rrggbbaa` — обычная запись и для человека, и для модели
 * (`LEGACY_BLOCK_COLOR_PATTERN` на бэке). Пока замер яркости знал только
 * `#rgb`/`#rrggbb`, всё остальное считалось белым: «сделай экран тёмным» с
 * `black` давало тёмный экран с тёмным текстом.
 *
 * `var(--…)` и незнакомое имя цвета возвращают null СОЗНАТЕЛЬНО: за
 * переменной может стоять любой цвет, и «предположить» здесь означало бы
 * перекрасить текст наугад. Неизмеримое — это «мерить нечем», а не «светлое».
 *
 * Альфа возвращается ОТДЕЛЬНЫМ полем и в яркость не подмешивается: над каким
 * фоном лежит полупрозрачный цвет, знает только вызывающий (см.
 * `blendHexOverBackground`, если фон известен, и `resolveScreenLayout`, где
 * полупрозрачная заливка тёмной сознательно не считается).
 */
export function parseCssColorToRgb(color: string): ParsedCssColor | null {
  const value = color.trim().toLowerCase();
  if (value.length === 0) return null;

  if (value === 'transparent') return { r: 0, g: 0, b: 0, alpha: 0 };

  if (value.startsWith('#')) {
    const clean = value.slice(1);
    if (!/^[0-9a-f]+$/.test(clean)) return null;
    const expand =
      clean.length === 3 || clean.length === 4
        ? clean
            .split('')
            .map((c) => c + c)
            .join('')
        : clean;
    if (expand.length !== 6 && expand.length !== 8) return null;
    const byte = (i: number): number => parseInt(expand.slice(i, i + 2), 16);
    return {
      r: byte(0),
      g: byte(2),
      b: byte(4),
      alpha: expand.length === 8 ? byte(6) / 255 : 1,
    };
  }

  const fn = /^(rgba?|hsla?)\(([^()]*)\)$/.exec(value);
  if (fn) {
    const nums = functionArgs(fn[2]);
    if (!nums || nums.length < 3) return null;
    const alpha = nums.length >= 4 ? Math.min(1, Math.max(0, nums[3])) : 1;
    if (fn[1].startsWith('rgb')) {
      // Проценты в rgb() уже поделены на 100 — вернуть их в 0..255.
      const chan = (n: number, raw: string): number =>
        raw.includes('%') ? n * 255 : n;
      const raws = fn[2].replace(/\//g, ' ').split(/[\s,]+/).filter(Boolean);
      return {
        r: Math.round(chan(nums[0], raws[0] ?? '')),
        g: Math.round(chan(nums[1], raws[1] ?? '')),
        b: Math.round(chan(nums[2], raws[2] ?? '')),
        alpha,
      };
    }
    // hsl: тон в градусах (проценты тут не бывают), насыщенность/светлота — доли.
    const [r, g, b] = hslToRgb(nums[0], nums[1], nums[2]);
    return { r, g, b, alpha };
  }

  const named = NAMED_COLORS[value];
  return named ? { r: named[0], g: named[1], b: named[2], alpha: 1 } : null;
}

/**
 * WCAG relative luminance for a CSS color string.
 * Returns 1 (white) for unparseable input.
 *
 * Имя историческое (WEB-872, когда мерились только коды цвета). С WEB-1805
 * (ревизия) разбор идёт через `parseCssColorToRgb`, то есть понимаются и
 * `rgb()/rgba()`, и `hsl()`, и имена базовых цветов — иначе замер сдавался
 * ровно на тех записях, которыми чаще всего просят «тёмный экран».
 *
 * Прозрачность НЕ учитывается: яркость полупрозрачного цвета зависит от того,
 * что под ним, а этого функция не знает. Вызывающий, которому важна альфа,
 * берёт её у `parseCssColorToRgb` сам.
 */
export function hexLuminance(hex: string): number {
  const parsed = parseCssColorToRgb(hex);
  if (!parsed) return 1;
  const lin = (c: number): number => {
    const v = c / 255;
    return v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * lin(parsed.r) + 0.7152 * lin(parsed.g) + 0.0722 * lin(parsed.b);
}

const ON_PRIMARY_DARK = '#1a1a1a';
const ON_PRIMARY_LIGHT = '#ffffff';
/**
 * Same threshold as backend's readableTextOn (backend/src/ai-funnels/prompts/
 * color-utils.ts) — both engines must agree on which primaries need dark vs
 * light button text so a preset never reads correctly on one side and wrong
 * on the other.
 */
const ON_PRIMARY_LUMINANCE_THRESHOLD = 0.179;

/**
 * Pick a contrast-safe button-text color for a given primary/fill hex.
 * WEB-872 п.2 — themeToStyle uses this to compute --color-on-primary for
 * every preset that doesn't declare theme.colors.onPrimary explicitly.
 */
export function pickOnPrimaryColor(primaryHex: string): string {
  const lum = hexLuminance(primaryHex);
  return lum > ON_PRIMARY_LUMINANCE_THRESHOLD ? ON_PRIMARY_DARK : ON_PRIMARY_LIGHT;
}

/** WCAG contrast ratio between two hex colors (1..21, порядок не важен). */
export function contrastRatio(a: string, b: string): number {
  const la = hexLuminance(a);
  const lb = hexLuminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/** #rgb / #rrggbb → [r, g, b]; null для всего остального (CSS-переменные и пр.). */
function parseHex(hex: string): [number, number, number] | null {
  const clean = hex.trim().replace(/^#/, '');
  const full =
    clean.length === 3
      ? clean[0] + clean[0] + clean[1] + clean[1] + clean[2] + clean[2]
      : clean;
  if (!/^[0-9a-fA-F]{6}$/.test(full)) return null;
  return [
    parseInt(full.slice(0, 2), 16),
    parseInt(full.slice(2, 4), 16),
    parseInt(full.slice(4, 6), 16),
  ];
}

const toHex = (rgb: readonly number[]): string =>
  `#${rgb.map((c) => Math.round(Math.min(255, Math.max(0, c))).toString(16).padStart(2, '0')).join('')}`;

/**
 * WEB-916 п.2 — довести цвет до нужного контраста с фоном, сохранив оттенок.
 *
 * QA намерил хвосты: звёзды рейтинга 1.9:1 (нужно ≥3.0 как нетекстовый
 * элемент), бейджи тарифов и ссылки условий 3.7-3.9:1 (нужно ≥4.5 как текст).
 * Фактический цвет ссылок задаёт палитра стиль-пака, поэтому подобрать один
 * литерал нельзя — считаем от палитры.
 *
 * Шагаем к чёрному на светлом фоне и к белому на тёмном. Непарсимый вход
 * возвращаем как есть: вёрстка не должна падать из-за CSS-переменной.
 */
export function darkenToContrast(color: string, background: string, minRatio: number): string {
  return darkenToContrastAll(color, [background], minRatio);
}

/**
 * WEB-916 п.2, ВОЗВРАТ ПРИЁМКИ (QA 02.08: ссылки условий 4.21 при пороге 4.5).
 *
 * Корень возврата: `darkenToContrast` держал порог против ОДНОГО фона —
 * `surface`. Но блок согласия рисуется без собственного фона, прямо на ФОНЕ
 * СТРАНИЦЫ (`background`, у градиентных тем — его крайние точки). Где
 * background темнее surface, гарантия рассыпалась:
 *
 *   candy-pop        vs surface #ffffff = 4.63, vs background #fff0f9 = 4.21
 *   cream-editorial  vs surface #fffdf8 = 4.72, vs background #f2f0e9 = 4.21
 *
 * 4.21 — ровно живой замер QA, то есть код и браузер сошлись.
 *
 * Поэтому порог держим против ВСЕХ поверхностей, на которых элемент может
 * оказаться, а не против первой. Направление (темнеть/светлеть) по-прежнему
 * задаёт ПЕРВАЯ поверхность — это базовая поверхность темы, и смешанные
 * светлая+тёмная наборы у наших тем не встречаются.
 */
export function darkenToContrastAll(
  color: string,
  backgrounds: readonly string[],
  minRatio: number,
): string {
  const rgb = parseHex(color);
  if (!rgb) return color;
  const surfaces = backgrounds.filter((bg) => parseHex(bg) !== null);
  if (surfaces.length === 0) return color;

  const holds = (candidate: string): boolean =>
    surfaces.every((bg) => contrastRatio(candidate, bg) >= minRatio);
  if (holds(color)) return color;

  // Светлый фон → идём к чёрному, тёмный → к белому.
  const target = hexLuminance(surfaces[0]) > 0.5 ? 0 : 255;
  // 40 шагов хватает: шаг ≈ 2.5% пути до цели, дальше уже упираемся в предел.
  for (let step = 1; step <= 40; step += 1) {
    const t = step / 40;
    const candidate = toHex(rgb.map((c) => c + (target - c) * t));
    if (holds(candidate)) return candidate;
  }
  return toHex([target, target, target]);
}

/**
 * WEB-1078 п.4 — alpha-composite `fg` (opacity `alpha`, 0..1) over `bg`, both
 * hex. Used to find the ACTUAL visible color of a semi-transparent block
 * background (blockBackgroundStyle.tsx: solid/translucent/blur/glass all go
 * through the same hexToRgba(color, opacity) recipe) so contrast can be
 * measured against what a person really sees, not the raw authored hex.
 *
 * Unparseable input returns null — caller decides the fallback, same
 * contract as `measurableBackgrounds` elsewhere in this file's callers.
 */
export function blendHexOverBackground(
  fgHex: string,
  alpha: number,
  bgHex: string,
): string | null {
  const fg = parseHex(fgHex);
  const bg = parseHex(bgHex);
  if (!fg || !bg) return null;
  const a = Math.min(1, Math.max(0, alpha));
  return toHex(fg.map((c, i) => a * c + (1 - a) * bg[i]));
}
